/**
 * series-helpers.ts
 *
 * Shared transforms for the Performance time-series charts.
 */

/**
 * Scope the plotted lane set to what was actually measured, keeping each run's
 * reading honest:
 *
 *  - A lane with NO reading in any run (never measured) is dropped entirely —
 *    no line, no legend chip.
 *  - A lane a given run DID measure keeps that run's value verbatim, including a
 *    measured 0.0 (a measured zero is a result, so the line reads as a flat zero
 *    rather than disappearing).
 *  - A run with no reading for a kept lane is left as a gap, so the lane's line
 *    simply begins (or resumes) at the runs that measured it — nothing is carried
 *    forward or backfilled across a run that did not measure the lane.
 *
 * A "reading" is any defined, non-null, numeric value; an unmeasured lane arrives
 * as an unset key (undefined), which is not a reading. `overall` (the headline
 * composite) and non-model keys (date/timestamp) are left untouched. Operates on
 * a copy; the input series is not mutated.
 */
export function holdLastCaptured<T extends Record<string, any>>(
  series: T[],
  models: string[],
): { series: T[]; models: string[] } {
  if (!series.length) return { series, models };

  const isReading = (v: any) =>
    v !== undefined && v !== null && !Number.isNaN(Number(v));

  // Keep only lanes measured (a real reading) in at least one run.
  const kept = models.filter((m) => series.some((p) => isReading(p[m])));

  // Values are preserved as measured; unmeasured runs stay gaps. Copy so the
  // caller's series is not mutated.
  const out = series.map((p) => ({ ...p }));
  return { series: out, models: kept };
}
